import { ReactNode, useCallback, useState } from 'react';
import useUserRoles from 'hooks/useUserRoles';
import { useParams } from 'react-router-dom';
import Insights from 'components/icons/insight';
import CalendarIcon from 'components/icons/calendar';
import InboxIcon from 'components/icons/inbox';
import SettingIcon from 'components/icons/setting';
import CircleIcon from 'components/icons/circle';
import User from 'components/icons/user';
import TasksIcon from 'components/icons/tasks';
import Dollar from 'components/icons/dollar';
import Users from 'components/icons/users';
import Book from 'components/icons/book';
import Building from 'components/icons/building';
import ButtonIcon from 'components/ButtonIcon';
import SidebarExpandIcon from 'components/icons/sidebarExpand';
import SidebarShrinkIcon from 'components/icons/sidebarShrink';
import SkillsIcon from 'components/icons/skills';
import { RenderIf } from 'react-rainbow-components';
import PhoneThin from 'components/icons/phone';
import Social from 'components/icons/social';
import ChatIcon from 'components/icons/chat';
import Shine from 'components/icons/shine';
import Opportunity from 'components/icons/opportunity';
import useOpenTasks from 'hooks/useOpenTasks';
import usePendingApprovalEventsCount from 'data/hooks/usePendingApprovalEventsCount';
import { HasRoles } from '@voxityai/auth/components';
import { auth } from 'data/firestore/firebase';
import BrainIcon from 'components/icons/brain';
import ReminderIcon from 'components/icons/reminder';
import DocumentIcon from 'components/icons/document';
import useAgentData from 'hooks/useAgentData';
import {
    Container,
    Header,
    Options,
    OptionsContainer,
    Separator,
    StyledLogoLarge,
    LogoContainer,
    DividerIcon,
    StyledLogoSmall,
    CustomBadge,
    LockClosedIcon,
} from './styled';
import Item from './Item';
import ItemSection from './ItemSection';

const getIcon = (isAnchored: boolean) => {
    if (isAnchored) {
        return <SidebarShrinkIcon />;
    }
    return <SidebarExpandIcon />;
};

// TODO: remove this when deploy instagram integration
const RenderIfAdminOrAppReview = ({ children }: { children: ReactNode }) => {
    const { isAdmin, user } = useUserRoles();
    if (isAdmin || user?.id === '5hZvP9chZ8f4sylDwJ1l9QY80gx1' || user?.id === 'tKCgrqpqV0eZrM8Fp84OoK6wDTK2') {
        return children;
    }
    return null;
};

const SideBar = () => {
    const { agentId } = useParams<{ agentId: string }>();
    const { isAppEnabled } = useAgentData();
    const [isExpanded, setExpanded] = useState(true);
    const [isShrinked, setShrinked] = useState(false);
    const { count } = usePendingApprovalEventsCount({
        limit: 100,
    });
    const { tasks } = useOpenTasks();
    const totalOpenedTasks = (tasks.length === 1000 ? '1K+' : tasks.length);

    const toggleShrink = useCallback(
        () => {
            setShrinked(!isShrinked);
            setExpanded(isShrinked);
        },
        [isShrinked],
    );

    const collapse = useCallback(
        () => {
            if (isShrinked) {
                setTimeout(() => setExpanded(false), 100);
            }
        },
        [isShrinked],
    );

    const expand = useCallback(
        () => {
            if (isShrinked && !isExpanded) {
                setTimeout(() => setExpanded(true), 100);
            }
        },
        [isExpanded, isShrinked],
    );

    const calendarNotifications = count === 100 ? '99+' : count;
    return (
        <Container
            isShrinked={isShrinked && !isExpanded}
            onMouseEnter={expand}
            onMouseLeave={collapse}
        >
            <Header className="rainbow-p-left_x-small rainbow-p-right_x-small">
                <LogoContainer>
                    <RenderIf isTrue={isShrinked && !isExpanded}>
                        <StyledLogoSmall />
                    </RenderIf>
                    <RenderIf isTrue={!isShrinked || isExpanded}>
                        <StyledLogoLarge />
                    </RenderIf>
                </LogoContainer>
                <RenderIf isTrue={!isShrinked || isExpanded}>
                    <ButtonIcon
                        icon={getIcon(!isShrinked)}
                        onClick={toggleShrink}
                    />
                </RenderIf>
            </Header>
            <OptionsContainer>
                <Options>
                    <Item to={`/${agentId}/tasks`} label="Tasks" icon={<TasksIcon />} customBadge={<CustomBadge color="red">New</CustomBadge>} notifications={totalOpenedTasks} isShrinked={isShrinked && !isExpanded} />
                    <Item to={`/${agentId}/inbox`} label="Inbox" icon={<InboxIcon />} customBadge={<CustomBadge color="gray">Deprecated</CustomBadge>} isShrinked={isShrinked && !isExpanded} />
                    <Item to={`/${agentId}/opportunities`} label="Opportunities" icon={<Opportunity />} isShrinked={isShrinked && !isExpanded} />
                    <Item to={`/${agentId}/calendar`} label="Calendar" icon={<CalendarIcon />} notifications={calendarNotifications ?? 0} isShrinked={isShrinked && !isExpanded} />
                    <Item to={`/${agentId}/customers`} label="Customers" icon={<User />} isShrinked={isShrinked && !isExpanded} />
                    <RenderIf isTrue={isAppEnabled('invoice-builder')}>
                        <Item
                            to={`/${agentId}/integrations/invoice-builder`}
                            label="Invoices"
                            icon={<DocumentIcon />}
                            isShrinked={isShrinked && !isExpanded}
                        />
                    </RenderIf>
                    <Item to={`/${agentId}/threads`} label="Conversations" icon={<ChatIcon />} isShrinked={isShrinked && !isExpanded} />
                    <Item to={`/${agentId}/workplaces`} label="Workplaces" icon={<Building />} isShrinked={isShrinked && !isExpanded} />
                    <Item to={`/${agentId}/voip/history`} label="Call History" icon={<PhoneThin />} isShrinked={isShrinked && !isExpanded} />
                    <ItemSection label="Insights" prefix={`/${agentId}/insights`} icon={<Insights />} isShrinked={isShrinked && !isExpanded}>
                        <Item to={`/${agentId}/insights/calls`} label="Calls" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/insights/affiliates`} label="Affiliates" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/insights/opportunities`} label="Opportunities" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/insights/social`} label="Social Media" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/insights/website_chatbot`} label="Website Chatbot" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/insights/topics`} label="Topics" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/insights/business`} label="Business" icon={<CircleIcon />} />
                    </ItemSection>
                    <RenderIf isTrue={!isShrinked || isExpanded}>
                        <Separator className="rainbow-p-left_x-small rainbow-p-right_x-small">Control Panel</Separator>
                    </RenderIf>
                    <RenderIf isTrue={isShrinked && !isExpanded}>
                        <DividerIcon />
                    </RenderIf>
                    <HasRoles auth={auth} agentId={agentId} allowedRoles={['owner', 'editor']}>
                        <Item to={`/${agentId}/billing`} label="Billing" icon={<Dollar />} isShrinked={isShrinked && !isExpanded} />
                    </HasRoles>
                    <ItemSection label="Settings" prefix={`/${agentId}/settings`} icon={<SettingIcon />} isShrinked={isShrinked && !isExpanded}>
                        <Item to={`/${agentId}/settings/company-info`} label="Company Info" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/settings/blocked-numbers`} label="Blocked Numbers" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/settings/phone-numbers`} label="Phone Numbers" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/settings/voip/notifications`} label="Call Notifications" icon={<CircleIcon />} />
                        <HasRoles auth={auth} allowedRoles={['admin']}>
                            <Item to={`/${agentId}/settings/conversations`} label="Conversations" icon={<CircleIcon />} />
                        </HasRoles>
                        <Item to={`/${agentId}/settings/topics`} label="Topics" icon={<CircleIcon />} />
                        <Item to={`/${agentId}/settings/tags`} label="Tags" icon={<CircleIcon />} />
                        <HasRoles auth={auth} allowedRoles={['admin']}>
                            <Item to={`/${agentId}/external-automations`} label="External Automations" icon={<CircleIcon />} />
                        </HasRoles>
                    </ItemSection>
                    <ItemSection label="Team" prefix={`/${agentId}/team`} icon={<Users />} isShrinked={isShrinked && !isExpanded}>
                        <HasRoles auth={auth} agentId={agentId} allowedRoles={['owner']}>
                            <Item to={`/${agentId}/team/members`} label="Members" icon={<CircleIcon />} />
                        </HasRoles>
                        <Item to={`/${agentId}/team/affiliates`} label="Affiliates" icon={<CircleIcon />} />
                    </ItemSection>
                    <HasRoles auth={auth} allowedRoles={['admin']}>
                        <Item to={`/${agentId}/memory`} label="Memory" icon={<BrainIcon />} isShrinked={isShrinked && !isExpanded} />
                    </HasRoles>
                    <RenderIfAdminOrAppReview>
                        <RenderIf isTrue={!isShrinked || isExpanded}>
                            <Separator className="rainbow-p-left_x-small rainbow-p-right_x-small">Assistant Channels</Separator>
                        </RenderIf>
                        <RenderIf isTrue={isShrinked && !isExpanded}>
                            <DividerIcon />
                        </RenderIf>
                        <Item label="AI Personas" to={`/${agentId}/llms`} icon={<Shine />} isShrinked={isShrinked && !isExpanded} />
                        <Item to={`/${agentId}/skills`} label="AI Skills" icon={<SkillsIcon />} isShrinked={isShrinked && !isExpanded} />
                    </RenderIfAdminOrAppReview>
                    <RenderIfAdminOrAppReview>
                        <RenderIf isTrue={!isShrinked || isExpanded}>
                            <Separator className="rainbow-p-left_x-small rainbow-p-right_x-small">Developer</Separator>
                        </RenderIf>
                        <RenderIf isTrue={isShrinked && !isExpanded}>
                            <DividerIcon />
                        </RenderIf>
                        <Item to={`/${agentId}/developer/api`} label="API Keys" icon={<LockClosedIcon />} isShrinked={isShrinked && !isExpanded} />
                        <Item to={`/${agentId}/developer/webhooks`} label="Webhooks" icon={<Social />} isShrinked={isShrinked && !isExpanded} />
                        <Item to={`/${agentId}/developer/reminders`} label="Reminders" icon={<ReminderIcon />} isShrinked={isShrinked && !isExpanded} />
                        <Item to="https://docs.voxity.ai/" label="Documentation" icon={<Book />} isShrinked={isShrinked && !isExpanded} />
                    </RenderIfAdminOrAppReview>
                </Options>
            </OptionsContainer>
        </Container>
    );
};

export default SideBar;
